import { memo, useEffect, useRef, useState } from "react";
import { Copy, RotateCcw } from "lucide-react";
import { cn } from "@/ui/utils";
import {
  Accordion
} from "../ui/accordion";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../ui/select";
import { Slider } from "../ui/slider";
import FileSheet, {
  FILE_SHEET_COMPACT_BUTTON_CLASSES,
  FILE_SHEET_COMPACT_INPUT_CLASSES,
  FILE_SHEET_FIELD_LABEL_CLASSES,
  FILE_SHEET_PRECISION_SLIDER_CLASSES,
  FileSheetControlRow,
  FileSheetSection,
  FileSheetSliderField,
  FileSheetSubsection,
  parseFileSheetNumberInput
} from "./FileSheet";
import FileMetadataSection from "./FileMetadataSection";
import FileStatusSection from "./FileStatusSection";

const fieldLabelClasses = FILE_SHEET_FIELD_LABEL_CLASSES;
const compactInputClasses = FILE_SHEET_COMPACT_INPUT_CLASSES;
const compactButtonClasses = FILE_SHEET_COMPACT_BUTTON_CLASSES;
const JOINT_CONTROL_SYNC_EPSILON = 0.001;
const JOINT_CONTROL_LOCAL_OVERRIDE_MS = 3500;

function jointControlValuesClose(left, right) {
  return Math.abs(Number(left) - Number(right)) <= JOINT_CONTROL_SYNC_EPSILON;
}

function isAngularJoint(joint) {
  const jointType = String(joint?.type || "").trim().toLowerCase();
  return jointType === "continuous" || jointType === "revolute";
}

function jointUnitLabel(joint) {
  return isAngularJoint(joint) ? "deg" : "m";
}

function formatJointValue(value, joint) {
  const scale = isAngularJoint(joint) ? 10 : 10000;
  const rounded = Math.round(Number(value) * scale) / scale;
  const safeValue = Number.isFinite(rounded) ? rounded : 0;
  return isAngularJoint(joint) ? `${safeValue}\u00b0` : `${safeValue} m`;
}

function formatSdfNumber(value, fallback = "0") {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return fallback;
  }
  const rounded = Math.round(numericValue * 1000) / 1000;
  return String(Object.is(rounded, -0) ? 0 : rounded);
}

function formatMotionCoordinate(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return "";
  }
  const rounded = Math.round(numericValue * 10000) / 10000;
  return String(Object.is(rounded, -0) ? 0 : rounded);
}

function motionPositionsClose(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length < 3 || b.length < 3) {
    return false;
  }
  return [0, 1, 2].every((index) => Math.abs(Number(a[index]) - Number(b[index])) <= 0.0005);
}

function clampJointInputValue(valueDeg, minValueDeg, maxValueDeg, fallbackValueDeg) {
  const numericValue = Number.isFinite(Number(valueDeg)) ? Number(valueDeg) : fallbackValueDeg;
  return Math.min(Math.max(numericValue, minValueDeg), Math.max(minValueDeg, maxValueDeg));
}

const UrdfJointRow = memo(function UrdfJointRow({
  joint,
  valueDeg,
  onValueChange
}) {
  const jointName = String(joint?.name || "").trim();
  const minValueDeg = Number.isFinite(Number(joint?.minValueDeg)) ? Number(joint.minValueDeg) : -180;
  const maxValueDeg = Number.isFinite(Number(joint?.maxValueDeg)) ? Number(joint.maxValueDeg) : 180;
  const safeValueDeg = clampJointInputValue(valueDeg, minValueDeg, maxValueDeg, 0);
  const unitLabel = jointUnitLabel(joint);
  const sliderStep = isAngularJoint(joint) ? 1 : 0.001;
  const pendingFrameRef = useRef(0);
  const pendingValueRef = useRef(safeValueDeg);
  const latestSafeValueRef = useRef(safeValueDeg);
  const localOverrideRef = useRef(false);
  const localOverrideTimeoutRef = useRef(0);
  const [liveValueDeg, setLiveValueDeg] = useState(safeValueDeg);

  const clearLocalOverrideTimeout = () => {
    if (localOverrideTimeoutRef.current && typeof window !== "undefined") {
      window.clearTimeout(localOverrideTimeoutRef.current);
    }
    localOverrideTimeoutRef.current = 0;
  };

  const releaseLocalOverride = (nextValueDeg = latestSafeValueRef.current) => {
    clearLocalOverrideTimeout();
    localOverrideRef.current = false;
    const normalizedValueDeg = clampJointInputValue(nextValueDeg, minValueDeg, maxValueDeg, latestSafeValueRef.current);
    pendingValueRef.current = normalizedValueDeg;
    setLiveValueDeg(normalizedValueDeg);
  };

  const holdLocalValueUntilParentSettles = (nextValueDeg) => {
    pendingValueRef.current = nextValueDeg;
    localOverrideRef.current = true;
    clearLocalOverrideTimeout();
    if (typeof window !== "undefined") {
      localOverrideTimeoutRef.current = window.setTimeout(() => {
        releaseLocalOverride();
      }, JOINT_CONTROL_LOCAL_OVERRIDE_MS);
    }
  };

  useEffect(() => {
    latestSafeValueRef.current = safeValueDeg;
    if (localOverrideRef.current) {
      if (jointControlValuesClose(safeValueDeg, pendingValueRef.current)) {
        releaseLocalOverride(safeValueDeg);
      }
      return;
    }
    pendingValueRef.current = safeValueDeg;
    setLiveValueDeg(safeValueDeg);
  }, [safeValueDeg]);

  useEffect(() => () => {
    if (pendingFrameRef.current && typeof cancelAnimationFrame === "function") {
      cancelAnimationFrame(pendingFrameRef.current);
    }
    clearLocalOverrideTimeout();
  }, []);

  const scheduleValueChange = (nextValueDeg, options = {}) => {
    pendingValueRef.current = nextValueDeg;
    if (typeof requestAnimationFrame !== "function") {
      onValueChange(joint, nextValueDeg, options);
      return;
    }
    if (pendingFrameRef.current) {
      return;
    }
    pendingFrameRef.current = requestAnimationFrame(() => {
      pendingFrameRef.current = 0;
      onValueChange(joint, pendingValueRef.current, options);
    });
  };

  const commitValue = (nextValueDeg, options = {}) => {
    const normalizedValueDeg = clampJointInputValue(nextValueDeg, minValueDeg, maxValueDeg, liveValueDeg);
    pendingValueRef.current = normalizedValueDeg;
    if (pendingFrameRef.current && typeof cancelAnimationFrame === "function") {
      cancelAnimationFrame(pendingFrameRef.current);
      pendingFrameRef.current = 0;
    }
    setLiveValueDeg(normalizedValueDeg);
    holdLocalValueUntilParentSettles(normalizedValueDeg);
    onValueChange(joint, normalizedValueDeg, options);
  };

  return (
    <FileSheetSliderField
      label={jointName || "Joint"}
      value={formatJointValue(liveValueDeg, joint)}
      onValueCommit={(nextValue) => {
        commitValue(parseFileSheetNumberInput(nextValue, {
          fallback: liveValueDeg,
          min: minValueDeg,
          max: maxValueDeg
        }));
      }}
      valueInputProps={{
        ariaLabel: `${jointName || "Joint"} value in ${unitLabel}`
      }}
    >
        <Slider
          className={cn(FILE_SHEET_PRECISION_SLIDER_CLASSES, "min-w-0")}
          min={minValueDeg}
          max={maxValueDeg}
          step={sliderStep}
          value={[liveValueDeg]}
          onValueChange={(nextValue) => {
            const nextValueDeg = clampJointInputValue(nextValue?.[0], minValueDeg, maxValueDeg, liveValueDeg);
            if (jointControlValuesClose(nextValueDeg, pendingValueRef.current)) {
              return;
            }
            setLiveValueDeg(nextValueDeg);
            holdLocalValueUntilParentSettles(nextValueDeg);
            scheduleValueChange(nextValueDeg, { scrub: true });
          }}
          onValueCommit={(nextValue) => {
            commitValue(nextValue?.[0], { scrub: true });
          }}
          aria-label={jointName || "Joint value"}
          title={`${formatJointValue(minValueDeg, joint)} to ${formatJointValue(maxValueDeg, joint)}`}
        />
    </FileSheetSliderField>
  );
});

const MotionCoordinateInput = memo(function MotionCoordinateInput({
  axis,
  value,
  disabled,
  onValueChange
}) {
  const safeValue = Number.isFinite(Number(value)) ? Number(value) : 0;
  const [draftValue, setDraftValue] = useState(() => formatMotionCoordinate(safeValue));

  useEffect(() => {
    setDraftValue(formatMotionCoordinate(safeValue));
  }, [safeValue]);

  const commitValue = (nextValue) => {
    const numericValue = Number(nextValue);
    const committedValue = Number.isFinite(numericValue) ? numericValue : safeValue;
    setDraftValue(formatMotionCoordinate(committedValue));
    onValueChange?.(committedValue);
  };

  return (
    <label className="block min-w-0">
      <span className={fieldLabelClasses}>{axis}</span>
      <Input
        type="number"
        step="0.001"
        inputMode="decimal"
        disabled={disabled}
        value={draftValue}
        onChange={(event) => {
          setDraftValue(event.target.value);
        }}
        onFocus={(event) => {
          event.currentTarget.select();
        }}
        onMouseUp={(event) => {
          event.preventDefault();
        }}
        onBlur={() => {
          commitValue(draftValue);
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.currentTarget.blur();
          }
        }}
        className={`${compactInputClasses} mt-1 text-right`}
        aria-label={`Target ${axis} coordinate`}
      />
    </label>
  );
});

function SdfValueField({ label, value }) {
  const displayValue = String(value ?? "");
  return (
    <div className="block min-w-0">
      <span className={fieldLabelClasses}>{label}</span>
      <div
        className="mt-1 min-h-7 truncate rounded-md border border-border/70 bg-muted/25 px-2 py-1.5 text-[11px] font-medium leading-4 text-foreground"
        title={displayValue}
      >
        {displayValue}
      </div>
    </div>
  );
}

function formatSdfMetadataItem(item, fields) {
  if (!item || typeof item !== "object") {
    return "";
  }
  return fields
    .map((field) => String(item?.[field] || "").trim())
    .filter(Boolean)
    .join(" / ");
}

function SdfMetadataList({ title, items, fields }) {
  const records = Array.isArray(items)
    ? items.map((item) => formatSdfMetadataItem(item, fields)).filter(Boolean)
    : [];
  if (!records.length) {
    return null;
  }
  return (
    <div className="space-y-1.5 rounded-md border border-border/80 bg-background/40 p-3">
      <span className={fieldLabelClasses}>{title}</span>
      <div className="space-y-1">
        {records.slice(0, 5).map((record, index) => (
          <div
            key={`${title}:${index}`}
            className="truncate text-[11px] font-medium leading-4 text-foreground"
            title={record}
          >
            {record}
          </div>
        ))}
        {records.length > 5 ? (
          <div className="text-xs text-muted-foreground">{records.length - 5} more</div>
        ) : null}
      </div>
    </div>
  );
}

export default function UrdfFileSheet({
  open,
  title = "URDF",
  sourceFormat = "urdf",
  showJoints = true,
  showMotion = true,
  isDesktop,
  width,
  selectedEntry = null,
  onOpenChange,
  onStartResize,
  joints,
  groupStates,
  activeGroupStateId,
  jointValues,
  onJointValueChange,
  onGroupStateSelect,
  onCopyJointAngles,
  onResetPose,
  motion = null,
  sdf = null,
  fileDownloadAvailable = false,
  viewerServerInfo = null,
  localFileOpenAvailable = false,
  fileAccessBusyKey = "",
  onOpenFileAsset,
  suppressDynamicMetadataStatus = false,
  statusItems = [],
  themeSections = null,
  openSectionIds = [],
  onOpenSectionIdsChange
}) {
  const isSdf = String(sourceFormat || "").trim().toLowerCase() === "sdf";
  const movableJoints = Array.isArray(joints) ? joints : [];
  const groupStatePresets = Array.isArray(groupStates) ? groupStates : [];
  const sdfInfo = sdf?.info && typeof sdf.info === "object" ? sdf.info : {};
  const sdfStaticMetadata = sdfInfo.staticMetadata && typeof sdfInfo.staticMetadata === "object"
    ? sdfInfo.staticMetadata
    : {};
  const sdfIncludes = Array.isArray(sdfStaticMetadata.includes) ? sdfStaticMetadata.includes : [];
  const sdfPlugins = Array.isArray(sdfStaticMetadata.plugins) ? sdfStaticMetadata.plugins : [];
  const sdfSensors = Array.isArray(sdfStaticMetadata.sensors) ? sdfStaticMetadata.sensors : [];
  const sdfLights = Array.isArray(sdfStaticMetadata.lights) ? sdfStaticMetadata.lights : [];
  const sdfPhysics = Array.isArray(sdfStaticMetadata.physics) ? sdfStaticMetadata.physics : [];
  const sdfNestedModelCount = Number.isFinite(Number(sdfStaticMetadata.nestedModelCount))
    ? Number(sdfStaticMetadata.nestedModelCount)
    : 0;
  const hasSdfMetadata = Boolean(
    sdfIncludes.length ||
    sdfPlugins.length ||
    sdfSensors.length ||
    sdfLights.length ||
    sdfPhysics.length
  );
  const motionEndEffectors = Array.isArray(motion?.endEffectors) ? motion.endEffectors : [];
  const motionPlanningGroups = Array.isArray(motion?.planningGroups) ? motion.planningGroups : [];
  const motionTargetFrames = Array.isArray(motion?.targetFrames) ? motion.targetFrames : [];
  const motionEnabled = showMotion && motion?.serverLive === true && motionEndEffectors.length > 0;
  const activeMotionEndEffectorName = String(motion?.activeEndEffectorName || motionEndEffectors[0]?.name || "").trim();
  const activeMotionPlanningGroupName = String(motion?.activePlanningGroupName || motionPlanningGroups[0]?.name || "").trim();
  const activeMotionTargetFrameName = String(motion?.activeTargetFrameName || motionTargetFrames[0] || "").trim();
  const motionTargetPosition = Array.isArray(motion?.targetPosition) ? motion.targetPosition : [0, 0, 0];
  const motionCurrentPosition = Array.isArray(motion?.currentPosition) ? motion.currentPosition : null;
  const moveit2Settings = motion?.moveit2 && typeof motion.moveit2 === "object" ? motion.moveit2 : {};
  const motionBusy = Boolean(motion?.solving);
  const motionActionsEnabled = motion?.actionsEnabled !== false;
  const motionServerStatus = motion?.serverLive ? "connected" : "offline";
  const motionSelectPoseActive = Boolean(motion?.selectPoseActive);
  const motionTargetMatchesCurrentPosition = motionCurrentPosition ? motionPositionsClose(motionTargetPosition, motionCurrentPosition) : true;
  const activeGroupStateValue = groupStatePresets.some((state) => String(state?.id || "").trim() === activeGroupStateId)
    ? activeGroupStateId
    : "__custom__";
  const activeGroupState = groupStatePresets.find((state) => String(state?.id || "").trim() === activeGroupStateValue);
  const activeGroupStateLabel = activeGroupStateValue === "__custom__" ? "custom" : String(activeGroupState?.label || activeGroupState?.name || activeGroupStateValue);

  return (
    <FileSheet
      open={open}
      title={title}
      isDesktop={isDesktop}
      width={width}
      onOpenChange={onOpenChange}
      onStartResize={onStartResize}
    >
      <Accordion
        type="multiple"
        value={openSectionIds}
        onValueChange={onOpenSectionIdsChange}
      >
        <FileStatusSection items={statusItems} />

        {isSdf ? (
          <FileSheetSection value="sdf" title="SDF">
              <div>
                <FileSheetSubsection title="Document" contentClassName="px-3">
                <div className="grid grid-cols-2 gap-2">
                  <SdfValueField label="Version" value={String(sdfInfo.version || "unknown")} />
                  <SdfValueField label="Document" value={String(sdfInfo.documentKind || "model")} />
                  {sdfInfo.worldName ? (
                    <SdfValueField label="World" value={String(sdfInfo.worldName)} />
                  ) : null}
                  <SdfValueField label="Frame mode" value={sdfInfo.nativeFrameSemantics ? "native" : "compat"} />
                  <SdfValueField label="Root link" value={String(sdfInfo.rootLink || "")} />
                  <SdfValueField label="Model" value={String(sdfInfo.modelName || title || "model")} />
                </div>
                </FileSheetSubsection>

                <FileSheetSubsection title="Counts" contentClassName="px-3">
                <div className="grid grid-cols-3 gap-2">
                  <SdfValueField label="Links" value={String(sdfInfo.linkCount ?? movableJoints.length)} />
                  <SdfValueField label="Joints" value={String(sdfInfo.jointCount ?? joints?.length ?? 0)} />
                  <SdfValueField label="Frames" value={String(sdfInfo.frameCount ?? 0)} />
                  <SdfValueField label="Includes" value={String(sdfIncludes.length)} />
                  <SdfValueField label="Plugins" value={String(sdfPlugins.length)} />
                  <SdfValueField label="Sensors" value={String(sdfSensors.length)} />
                  <SdfValueField label="Lights" value={String(sdfLights.length)} />
                  <SdfValueField label="Physics" value={String(sdfPhysics.length)} />
                  <SdfValueField label="Nested models" value={formatSdfNumber(sdfNestedModelCount)} />
                  <SdfValueField label="Unsupported geom." value={`${formatSdfNumber(sdfInfo.unsupportedVisualCount)} / ${formatSdfNumber(sdfInfo.unsupportedCollisionCount)}`} />
                </div>
                </FileSheetSubsection>

                {hasSdfMetadata ? (
                  <FileSheetSubsection title="Metadata" contentClassName="px-3">
                    <SdfMetadataList title="Includes" items={sdfIncludes} fields={["name", "uri"]} />
                    <SdfMetadataList title="Plugins" items={sdfPlugins} fields={["name", "filename"]} />
                    <SdfMetadataList title="Sensors" items={sdfSensors} fields={["name", "type"]} />
                    <SdfMetadataList title="Lights" items={sdfLights} fields={["name", "type"]} />
                    <SdfMetadataList title="Physics" items={sdfPhysics} fields={["name", "type", "default"]} />
                  </FileSheetSubsection>
                ) : null}
              </div>
          </FileSheetSection>
        ) : null}
        {motionEnabled ? (
          <FileSheetSection value="motion" title="MoveIt2">
              <div>
                <FileSheetSubsection title="Status">
                <FileSheetControlRow>
                <div className="grid grid-cols-2 gap-2">
                  <label className="block min-w-0">
                    <span className={fieldLabelClasses}>SRDF</span>
                    <Input
                      value={motion?.srdf?.srdf || motion?.srdf?.srdfHash || "linked"}
                      readOnly
                      disabled
                      className={`${compactInputClasses} mt-1`}
                      aria-label="SRDF status"
                    />
                  </label>
                  <label className="block min-w-0">
                    <span className={fieldLabelClasses}>MoveIt2 server</span>
                    <Input
                      value={motionServerStatus}
                      readOnly
                      disabled
                      className={`${compactInputClasses} mt-1`}
                    aria-label="MoveIt2 server status"
                  />
                </label>
              </div>
                </FileSheetControlRow>
                </FileSheetSubsection>

                <FileSheetSubsection title="Target">
                <FileSheetControlRow>
                <div className="grid grid-cols-2 gap-2">
                  <label className="block min-w-0">
                    <span className={fieldLabelClasses}>Planning group</span>
                    <Select
                      value={activeMotionPlanningGroupName}
                      disabled={motionBusy || motionPlanningGroups.length <= 1}
                      onValueChange={(value) => {
                        motion?.onMoveIt2SettingChange?.("activePlanningGroupName", value);
                      }}
                    >
                      <SelectTrigger size="sm" className="mt-1 h-7 !text-[11px]">
                        <SelectValue placeholder="Select group" />
                      </SelectTrigger>
                      <SelectContent>
                        {motionPlanningGroups.map((group) => {
                          const name = String(group?.name || "").trim();
                          return name ? (
                            <SelectItem key={name} value={name}>
                              {name}
                            </SelectItem>
                          ) : null;
                        })}
                      </SelectContent>
                    </Select>
                  </label>
                  <label className="block min-w-0">
                    <span className={fieldLabelClasses}>End effector</span>
                  <Select
                    value={activeMotionEndEffectorName}
                    disabled={motionBusy || motionEndEffectors.length <= 1}
                    onValueChange={(value) => {
                      motion?.onEndEffectorChange?.(value);
                    }}
                  >
                    <SelectTrigger size="sm" className="mt-1 h-7 !text-[11px]">
                      <SelectValue placeholder="Select end effector" />
                    </SelectTrigger>
                    <SelectContent>
                      {motionEndEffectors.map((endEffector) => {
                        const name = String(endEffector?.name || "").trim();
                        return name ? (
                          <SelectItem key={name} value={name}>
                            {name}
                          </SelectItem>
                        ) : null;
                      })}
                    </SelectContent>
                  </Select>
                  </label>
                  <label className="block min-w-0">
                    <span className={fieldLabelClasses}>Target frame</span>
                    <Select
                      value={activeMotionTargetFrameName}
                      disabled={motionBusy || motionTargetFrames.length <= 1}
                      onValueChange={(value) => {
                        motion?.onMoveIt2SettingChange?.("targetFrame", value);
                      }}
                    >
                      <SelectTrigger size="sm" className="mt-1 h-7 !text-[11px]">
                        <SelectValue placeholder="Select frame" />
                      </SelectTrigger>
                      <SelectContent>
                        {motionTargetFrames.map((frame) => {
                          const name = String(frame || "").trim();
                          return name ? (
                            <SelectItem key={name} value={name}>
                              {name}
                            </SelectItem>
                          ) : null;
                        })}
                      </SelectContent>
                  </Select>
                  </label>
                </div>
                </FileSheetControlRow>

                <FileSheetControlRow>
                <div className="grid grid-cols-3 gap-2">
                  {["X", "Y", "Z"].map((axis, index) => (
                    <MotionCoordinateInput
                      key={axis}
                      axis={axis}
                      value={motionTargetPosition[index]}
                      disabled={motionBusy}
                      onValueChange={(nextValue) => {
                        motion?.onTargetPositionChange?.(index, nextValue);
                      }}
                    />
                  ))}
                </div>
                </FileSheetControlRow>
                </FileSheetSubsection>

                <FileSheetSubsection title="Solver">
                <FileSheetControlRow>
                <div className="grid grid-cols-3 gap-2">
                  <label className="block min-w-0">
                    <span className={fieldLabelClasses}>IK timeout</span>
                    <Input
                      type="number"
                      step="0.01"
                      min="0.001"
                      value={moveit2Settings.ikTimeout ?? 0.05}
                      disabled={motionBusy}
                      onChange={(event) => motion?.onMoveIt2SettingChange?.("ikTimeout", event.target.value)}
                      className={`${compactInputClasses} mt-1 text-right`}
                    />
                  </label>
                  <label className="block min-w-0">
                    <span className={fieldLabelClasses}>IK attempts</span>
                    <Input
                      type="number"
                      step="1"
                      min="1"
                      value={moveit2Settings.ikAttempts ?? 1}
                      disabled={motionBusy}
                      onChange={(event) => motion?.onMoveIt2SettingChange?.("ikAttempts", event.target.value)}
                      className={`${compactInputClasses} mt-1 text-right`}
                    />
                  </label>
                  <label className="block min-w-0">
                    <span className={fieldLabelClasses}>Tolerance</span>
                    <Input
                      type="number"
                      step="0.001"
                      min="0.0001"
                      value={moveit2Settings.ikTolerance ?? 0.002}
                      disabled={motionBusy}
                      onChange={(event) => motion?.onMoveIt2SettingChange?.("ikTolerance", event.target.value)}
                      className={`${compactInputClasses} mt-1 text-right`}
                    />
                  </label>
                </div>
                </FileSheetControlRow>
                </FileSheetSubsection>

                <FileSheetSubsection title="Planning">
                <FileSheetControlRow>
                <div className="grid grid-cols-2 gap-2">
                  <label className="block min-w-0">
                    <span className={fieldLabelClasses}>Planning pipeline</span>
                    <Input
                      value={moveit2Settings.planningPipeline ?? "ompl"}
                      disabled={motionBusy}
                      onChange={(event) => motion?.onMoveIt2SettingChange?.("planningPipeline", event.target.value)}
                      className={`${compactInputClasses} mt-1`}
                    />
                  </label>
                  <label className="block min-w-0">
                    <span className={fieldLabelClasses}>Planner ID</span>
                    <Input
                      value={moveit2Settings.plannerId ?? "RRTConnectkConfigDefault"}
                      disabled={motionBusy}
                      onChange={(event) => motion?.onMoveIt2SettingChange?.("plannerId", event.target.value)}
                      className={`${compactInputClasses} mt-1`}
                    />
                  </label>
                </div>
                </FileSheetControlRow>

                <FileSheetControlRow>
                <div className="grid grid-cols-3 gap-2">
                  <label className="block min-w-0">
                    <span className={fieldLabelClasses}>Plan time</span>
                    <Input
                      type="number"
                      step="0.1"
                      min="0.1"
                      value={moveit2Settings.planningTime ?? 1}
                      disabled={motionBusy}
                      onChange={(event) => motion?.onMoveIt2SettingChange?.("planningTime", event.target.value)}
                      className={`${compactInputClasses} mt-1 text-right`}
                    />
                  </label>
                  <label className="block min-w-0">
                    <span className={fieldLabelClasses}>Velocity</span>
                    <Input
                      type="number"
                      step="0.05"
                      min="0.01"
                      max="1"
                      value={moveit2Settings.maxVelocityScalingFactor ?? 1}
                      disabled={motionBusy}
                      onChange={(event) => motion?.onMoveIt2SettingChange?.("maxVelocityScalingFactor", event.target.value)}
                      className={`${compactInputClasses} mt-1 text-right`}
                    />
                  </label>
                  <label className="block min-w-0">
                    <span className={fieldLabelClasses}>Acceleration</span>
                    <Input
                      type="number"
                      step="0.05"
                      min="0.01"
                      max="1"
                      value={moveit2Settings.maxAccelerationScalingFactor ?? 1}
                      disabled={motionBusy}
                      onChange={(event) => motion?.onMoveIt2SettingChange?.("maxAccelerationScalingFactor", event.target.value)}
                      className={`${compactInputClasses} mt-1 text-right`}
                    />
                  </label>
                </div>
                </FileSheetControlRow>
                </FileSheetSubsection>

                <FileSheetSubsection title="Actions">
                <FileSheetControlRow>
                <div className="flex flex-wrap gap-1.5">
                  <Button
                    type="button"
                    variant={motionSelectPoseActive ? "secondary" : "outline"}
                    size="sm"
                    className={compactButtonClasses}
                    disabled={motionBusy || !motionActionsEnabled}
                    onClick={() => {
                      if (motionSelectPoseActive) {
                        motion?.onCancelSelectPose?.();
                        return;
                      }
                      motion?.onSelectPose?.();
                    }}
                    aria-pressed={motionSelectPoseActive}
                  >
                    <span>Select Pose</span>
                  </Button>
                  {!motionTargetMatchesCurrentPosition ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className={compactButtonClasses}
                      disabled={motionBusy}
                      onClick={() => {
                        motion?.onUseCurrentPosition?.();
                      }}
                    >
                      <span>Reset</span>
                    </Button>
                  ) : null}
                  <Button
                    type="button"
                    variant="default"
                    size="sm"
                    className={compactButtonClasses}
                    disabled={motionBusy || !motionActionsEnabled || motionTargetMatchesCurrentPosition}
                    onClick={() => {
                      void motion?.onSolve?.();
                    }}
                  >
                    <span>{motionBusy ? "Solving..." : "Solve pose"}</span>
                  </Button>
                  <Button
                    type="button"
                    variant="default"
                    size="sm"
                    className={compactButtonClasses}
                    disabled={motionBusy || !motionActionsEnabled || motionTargetMatchesCurrentPosition}
                    onClick={() => {
                      void motion?.onPlan?.();
                    }}
                  >
                    <span>{motionBusy ? "Planning..." : "Plan to pose"}</span>
                  </Button>
                </div>
                </FileSheetControlRow>
                </FileSheetSubsection>
              </div>
          </FileSheetSection>
        ) : null}
        {showJoints ? (
        <FileSheetSection value="joints" title="Joints">
            {movableJoints.length ? (
              <>
                <FileSheetSubsection title="Controls">
                  {groupStatePresets.length ? (
                    <FileSheetControlRow label="Group state">
                      <Select
                        value={activeGroupStateValue}
                        onValueChange={(value) => {
                          if (value === "__custom__") {
                            return;
                          }
                          const groupState = groupStatePresets.find((candidate) => String(candidate?.id || "").trim() === value);
                          if (groupState) {
                            onGroupStateSelect?.(groupState);
                          }
                        }}
                      >
                        <SelectTrigger size="sm" className="h-7 !text-[11px]">
                          <span className="truncate">{activeGroupStateLabel}</span>
                        </SelectTrigger>
                        <SelectContent>
                          {groupStatePresets.map((groupState) => {
                            const groupStateId = String(groupState?.id || "").trim();
                            const groupStateName = String(groupState?.label || groupState?.name || "").trim() || "State";
                            return (
                              <SelectItem key={groupStateId} value={groupStateId}>
                                {groupStateName}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </FileSheetControlRow>
                  ) : null}
                  <FileSheetControlRow>
                  <div className="flex flex-wrap gap-1.5">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className={compactButtonClasses}
                      onClick={onResetPose}
                    >
                      <RotateCcw className="h-3.5 w-3.5" strokeWidth={2} aria-hidden="true" />
                      <span>Reset pose</span>
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className={compactButtonClasses}
                      onClick={() => {
                        void onCopyJointAngles?.();
                      }}
                    >
                      <Copy className="h-3.5 w-3.5" strokeWidth={2} aria-hidden="true" />
                      <span>{isSdf ? "Copy values" : "Copy angles"}</span>
                    </Button>
                  </div>
                  </FileSheetControlRow>
                </FileSheetSubsection>
                <FileSheetSubsection title="Values">
                {movableJoints.map((joint) => (
                  <UrdfJointRow
                    key={joint.name}
                    joint={joint}
                    valueDeg={jointValues?.[joint.name] ?? joint?.defaultValueDeg ?? 0}
                    onValueChange={onJointValueChange}
                  />
                ))}
                </FileSheetSubsection>
              </>
            ) : (
              <p className="px-3 py-2 text-xs text-muted-foreground">No movable joints are available.</p>
            )}
        </FileSheetSection>
        ) : null}
        {themeSections}
        <FileMetadataSection
          entry={selectedEntry}
          fileDownloadAvailable={fileDownloadAvailable}
          viewerServerInfo={viewerServerInfo}
          localFileOpenAvailable={localFileOpenAvailable}
          fileAccessBusyKey={fileAccessBusyKey}
          onOpenFileAsset={onOpenFileAsset}
          suppressDynamicStatus={suppressDynamicMetadataStatus}
        />
      </Accordion>
    </FileSheet>
  );
}
